/// <amd-module name="Feed/Legacy/Components/PackageGrid" />
import * as React from "react";

import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { CheckboxVisibility, ColumnActionsMode, ConstrainMode, DetailsListLayoutMode, IColumn } from "OfficeFabric/DetailsList";
import { IObjectWithKey, Selection, SelectionMode } from "OfficeFabric/Selection";

import { IVssPageContext } from "VSS/Platform/Context";

import { Spinner } from "VSSUI/Spinner";
import * as Utils_Date from "VSSUI/Utilities/Date";
import { VssDetailsList, VssDetailsListRowStyle } from "VSSUI/VssDetailsList";

import { FeedView, Package, PackageMetrics, PackageVersion } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { MessageState } from "Feed/Common/Types/IFeedMessage";
import { MetricType } from "Feed/Common/Types/MetricType";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { IPackageSelectionChangedPayload } from "Feed/Legacy/Common/ActionPayloads";
import * as ContextMenuNavigationCommands from "Feed/Legacy/Common/ContextMenuNavigationCommands";
import { DescriptionGridCell } from "Feed/Legacy/Components/DescriptionGridCell";
import { LoadingContainer } from "Feed/Legacy/Components/LoadingContainer";
import { IMetricProps, Metrics } from "Feed/Legacy/Components/Metric";
import { NoResultsPane } from "Feed/Legacy/Components/NoResultsPane";
import { PackageNameGridCell } from "Feed/Legacy/Components/PackageNameGridCell";
import { PackageUpstreamSource } from "Feed/Legacy/Components/PackageUpstreamSource";
import { ViewsGridCell } from "Feed/Legacy/Components/ViewsGridCell";
import * as CommandGetters from "Feed/Legacy/Helpers/CommandGetters";
import * as PackageCommandMenuItem from "Feed/Legacy/Helpers/PackageCommandToContextMenuItem";
import { isPublicFeed } from "Feed/Legacy/Helpers/ProjectHelper";
import { getPackageDetailsPageUrl } from "Feed/Legacy/Helpers/UrlHelper";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { IvyKey } from "Feed/Legacy/Protocols/Constants/Constants";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

import { HubWebPageDataService } from "../DataServices/WebPageDataService";

export interface IPackageGridProps {
    pageContext: IVssPageContext;
    feed: Feed;
    packages: Package[];
    pageSize: number;
    requestedPackageCount: number;
    nextPageLoading: boolean;
    filterLoading: boolean;
    protocolMap: { [key: string]: IPackageProtocol };
    upstreamSourceEnabled: boolean;
    messageState: MessageState;
    isMultiPromotePanelOpen: boolean;
    feedViews: FeedView[];
    selectedPackages: Package[];
    metricsEnabled: boolean;
    /* package id maps to package metrics */
    packageIdMetricsMap: { [key: string]: PackageMetrics };
}

export const PackageGrid: React.StatelessComponent<IPackageGridProps> = (props: IPackageGridProps) => {

    const selection: Selection = new Selection({
        canSelectItem: (item: IObjectWithKey): boolean => {
            const row: Package = item as Package;
            if (row && row.versions[0].isDeleted !== true) {
                return true;
            }
            return false;
        },
        getKey: (item: IObjectWithKey, index: number): string => {
            return getPackageSelectionId(item as Package);
        },
        onSelectionChanged: (): void => {
            onSelectionChangedCallback();
        }
    });

    return (
        <div className="package-grid">
            {props.packages && props.packages.length > 0 ? (
                <div>
                    <LoadingContainer isLoading={props.filterLoading}>
                        <VssDetailsList
                            setKey={props.feed.id}
                            className="package-details-list"
                            actionsColumnKey="name"
                            rowStyle={VssDetailsListRowStyle.twoLine}
                            allocateSpaceForActionsButtonWhileHidden={false}
                            getMenuItems={getContextMenuItems}
                            shouldDisplayActions={shouldDisplayCommands}
                            ariaLabel={PackageResources.PackageListAriaLabel}
                            ariaLabelForListHeader={PackageResources.PackageHeadersAriaLabel}
                            ariaLabelForSelectionColumn={PackageResources.DetailsList_SelectionColumn_AriaLabel}
                            ariaLabelForSelectAllCheckbox={PackageResources.DetailsList_SelectAll_AriaLabel}
                            checkButtonAriaLabel={PackageResources.DetailsList_Select_AriaLabel}
                            getRowAriaLabel={getRowAriaLabel}
                            items={props.packages}
                            columns={getColumns()}
                            constrainMode={ConstrainMode.unconstrained}
                            selectionMode={
                                props.selectedPackages.some(pkg => pkg.protocolType.toLowerCase() === IvyKey)
                                    ? SelectionMode.single
                                    : SelectionMode.multiple
                            }
                            selection={selection}
                            checkboxVisibility={CheckboxVisibility.onHover}
                            layoutMode={DetailsListLayoutMode.justified}
                            onRenderItemColumn={renderItemColumn}
                            onRowDidMount={loadNextPage}
                            selectionPreservedOnEmptyClick={true}
                        />
                    </LoadingContainer>
                    <div className="loading-placeholder">{props.nextPageLoading ? <Spinner /> : null}</div>
                </div>
            ) : (
                    getNoPackagesMessage()
                )}
        </div>
    );

    function getPackageSelectionId(pkg: Package): string {
        return pkg ? pkg.id : undefined;
    }

    function getRowAriaLabel(item: Package): string {
        return item && item.versions ? item.name + " " + item.versions[0].version : null;
    }

    /* DetailsList helper methods */
    function getColumns(): IColumn[] {
        let columns: IColumn[] = [];
        columns = [
            {
                key: "name",
                fieldName: "name",
                name: PackageResources.PackageNameColumn,
                minWidth: 200,
                maxWidth: 450,
                isResizable: true,
                className: "package-name-cell package-grid-cell",
                headerClassName: "package-name-header package-header",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: Package, index: number, column: IColumn) => {
                    const protocol: IPackageProtocol = props.protocolMap[item.protocolType];
                    return (
                        <PackageNameGridCell
                            feedName={props.feed.name}
                            pkg={item}
                            protocol={protocol}
                            actions={{ PackageSelected: Actions.PackageSelected }}
                            packageHref={getPackageDetailsPageUrl(
                                props.pageContext,
                                props.feed.name,
                                item,
                                item.versions[0].version
                            )}
                        />
                    );
                }
            } as IColumn
        ];

        columns.push({
            key: "views",
            fieldName: "views",
            name: PackageResources.ReleaseViewsColumn,
            minWidth: 185,
            maxWidth: 185,
            isResizable: true,
            headerClassName: "package-views-header package-header",
            className: "package-views-cell package-grid-cell",
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: Package, index: number, column: IColumn) => {
                return <ViewsGridCell packageVersion={item.versions[0]} />;
            }
        } as IColumn);

        if (props.upstreamSourceEnabled && shouldDisplaySource(props.feed)) {
            columns.push({
                key: "source",
                fieldName: "source",
                name: PackageResources.UpstreamSourceColumn,
                minWidth: 100,
                maxWidth: 150,
                isResizable: true,
                className: "package-source-cell package-grid-cell",
                columnActionsMode: ColumnActionsMode.disabled,
                headerClassName: "package-header",
                onRender: (pkg: Package, index: number, column: IColumn): JSX.Element => {
                    return (
                        <PackageUpstreamSource
                            pageContext={props.pageContext}
                            packageSummary={pkg}
                            packageVersion={pkg.versions[0]}
                            upstreamSources={props.feed.upstreamSources}
                        />
                    );
                }
            } as IColumn);
        }

        columns.push({
            key: "lastpushed",
            fieldName: "lastpushed",
            name: PackageResources.LastPushedColumn,
            minWidth: 100,
            maxWidth: 130,
            isResizable: true,
            isSorted: false,
            headerClassName: "package-header",
            className: "package-pushed-cell package-grid-cell",
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: Package, index: number, column: IColumn) => {
                const publishedDate = new Date(item.versions[0].publishDate.toString());
                const lastPushedDate = Utils_Date.ago(publishedDate);
                return <span>{lastPushedDate} </span>;
            },
            onColumnClick: (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
                // placeholder when sorting is implemented
                return;
            }
        } as IColumn);

        columns.push({
            key: "description",
            fieldName: "description",
            name: PackageResources.DescriptionColumn,
            minWidth: 200,
            maxWidth: 500,
            isResizable: true,
            className: "package-description-cell package-grid-cell",
            headerClassName: "package-header",
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: Package, index: number, column: IColumn) => {
                return <DescriptionGridCell item={item} />;
            }
        } as IColumn);

        if (props.metricsEnabled) {
            columns.push({
                key: "downloads",
                fieldName: "downloads",
                name: PackageResources.PackageGrid_DownloadsColumn,
                minWidth: 80,
                maxWidth: 110,
                isResizable: true,
                className: "package-downloads-cell package-grid-cell",
                headerClassName: "package-header",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: Package, index: number, column: IColumn) => {
                    const metrics = props.packageIdMetricsMap[item.id];
                    const MetricProps: IMetricProps = {
                        metrics: metrics,
                        metricType: MetricType.DownloadCount
                    };
                    return <Metrics {...MetricProps} />;
                }
            } as IColumn);

            if (!isPublicFeed(props.feed)) {
                columns.push({
                    key: "users",
                    fieldName: "users",
                    name: PackageResources.PackageGrid_UsersColumn,
                    minWidth: 80,
                    maxWidth: 110,
                    isResizable: true,
                    className: "package-users-cell package-grid-cell",
                    headerClassName: "package-header",
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: (item: Package, index: number, column: IColumn) => {
                        const metrics = props.packageIdMetricsMap[item.id];
                        const MetricProps: IMetricProps = {
                            metrics: metrics,
                            metricType: MetricType.UniqueUsers
                        };
                        return <Metrics {...MetricProps} />;
                    }
                } as IColumn);
            }
        }

        return columns;
    }

    function shouldDisplaySource(feed: Feed): boolean {
        if (feed.project != null) {
            var webPageDataService = props.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
            if (!webPageDataService.isProjectScopedUpstreamsEnabled()) {
                return false;
            }
            if (isPublicFeed(feed)) {
                return false;
            }
        }
        return true;
    }

    function // tslint:disable-next-line:no-any
        renderItemColumn(item: Package, index: number, column: IColumn): any {
        return column.onRender;
    }

    function loadNextPage(item: Package, index: number): void {
        const rowNumber = index + 1;
        const isLastRow = rowNumber === props.requestedPackageCount;
        if (isLastRow) {
            Actions.NextPackagePageRequested.invoke({});
        }
    }

    function shouldDisplayCommands(item: Package): boolean {
        if (item.versions[0].isDeleted) {
            return false;
        }

        if (selection.count === 0) {
            return true;
        }

        const commands: IContextualMenuItem[] = getPackageCommands(item);
        return commands.length > 0;
    }

    function getContextMenuItems(item: Package): IContextualMenuItem[] {
        // reset selection if context menu if invoked on package that is not selected
        const selectionKey: string = getPackageSelectionId(item);
        if (selection.getSelectedCount() > 0 && !selection.isKeySelected(selectionKey)) {
            Actions.PackageSelectionChangedInPackageGrid.invoke({ selectedPackages: [] });
        }

        return getPackageCommands(item);
    }

    function getPackageCommands(item: Package): IContextualMenuItem[] {
        let commands: IPackageCommand[] = [];
        let contextMenuItems: IContextualMenuItem[] = [];
        const packageCommandsGetter: CommandGetters.PackageCommandsGetter = new CommandGetters.PackageCommandsGetter();
        const contextItemsGetter = new PackageCommandMenuItem.ContextualMenuItemsGetter();
        let versionString: string;

        // if multiple packages were selected, determine if a selected package was clicked
        if (selection.getSelectedCount() > 1 && selection.isKeySelected(item.id)) {
            commands = packageCommandsGetter.getMultipleSelectionItems(
                props.feed,
                props.protocolMap,
                selection.getSelection() as Package[],
                props.pageContext
            );
        } else {
            const feedName = props.feed.view ? props.feed.name + "@" + props.feed.view.name : props.feed.name;
            contextMenuItems = ContextMenuNavigationCommands.getNavigationCommands(props.pageContext, feedName, item, {
                PackageSelected: Actions.PackageSelected
            });
            commands = packageCommandsGetter.getSingleSelectionItems(
                props.feed,
                props.protocolMap[item.protocolType],
                item,
                item.versions[0] as PackageVersion,
                true,
                props.pageContext
            );
            versionString = item.versions[0].version;
        }

        const protocolContextMenuItems = contextItemsGetter.GetContextMenuItems(commands, versionString);
        contextMenuItems.push(...protocolContextMenuItems);

        return contextMenuItems;
    }

    function getNoPackagesMessage(): React.ReactNode {
        switch (props.messageState) {
            case MessageState.EmptyView:
                return (
                    <NoResultsPane
                        pageContext={props.pageContext}
                        header={PackageResources.ReleaseViewsHeader}
                        subheader={PackageResources.ReleaseViewsSubHeader}
                        link={PackageResources.ReleaseViewsLearnMoreLink}
                        linkText={PackageResources.ReleaseViewsLearnMoreLinkText}
                        iconClass={"bowtie-arrow-up"}
                    />
                );
            case MessageState.EmptyCache:
                return (
                    <NoResultsPane
                        pageContext={props.pageContext}
                        header={PackageResources.UpstreamCacheHeader}
                        subheader={PackageResources.UpstreamCacheSubheader}
                        link={PackageResources.UpstreamCacheLearnMoreLink}
                        linkText={PackageResources.UpstreamCacheLearnMoreLinkText}
                        iconClass={"bowtie-cloud-fill"}
                    />
                );
            case MessageState.EmptySource:
                return (
                    <NoResultsPane
                        pageContext={props.pageContext}
                        header={PackageResources.UpstreamSavedHeader}
                        subheader={PackageResources.UpstreamSavedSubheader}
                        link={PackageResources.UpstreamCacheLearnMoreLink}
                        linkText={PackageResources.UpstreamCacheLearnMoreLinkText}
                        iconClass={"bowtie-cloud-fill"}
                    />
                );
            case MessageState.EmptyFilter:
                return (
                    <NoResultsPane
                        pageContext={props.pageContext}
                        header={PackageResources.PackageGrid_NoPackagesMessage}
                        subheader={""}
                        link={""}
                        linkText={""}
                        iconClass={"bowtie-search"}
                    />
                );
            default:
                return null;
        }
    }

    function onSelectionChangedCallback(): void {
        const selectedPackages: Package[] = selection.getSelection() as Package[];
        const selectionChangedPayload: IPackageSelectionChangedPayload = {
            selectedPackages
        } as IPackageSelectionChangedPayload;

        Actions.PackageSelectionChangedInPackageGrid.invoke(selectionChangedPayload);
    }
};
